import React, { useState, useEffect } from 'react';
import { API_URL } from '../../../config/api';

/**
 * FirmNotesLogWidget — compact version of NotesTab. Shows the communication
 * log with an inline "add note" affordance instead of an always-open form.
 * Data contract: GET/POST /firms/:id/notes ({id,noteText,createdBy,createdAt}),
 * PUT/DELETE /notes/:id.
 */
export default function FirmNotesLogWidget({ firmId }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    const token = localStorage.getItem('authToken');
    const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    useEffect(() => { fetchNotes(); /* eslint-disable-next-line */ }, [firmId]);

    const fetchNotes = async () => {
        try {
            const res = await fetch(`${API_URL}/firms/${firmId}/notes`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setNotes(await res.json());
        } catch (e) { console.error('Error fetching notes:', e); }
        finally { setLoading(false); }
    };

    const handleAdd = async () => {
        if (!newNote.trim()) return;
        try {
            setSaving(true);
            const createdBy = localStorage.getItem('userName') || 'Anonymous';
            const res = await fetch(`${API_URL}/firms/${firmId}/notes`, {
                method: 'POST', headers: authHeaders,
                body: JSON.stringify({ noteText: newNote, createdBy }),
            });
            if (!res.ok) throw new Error('Failed to save note');
            setNewNote(''); setAdding(false); fetchNotes();
        } catch (e) { alert(e.message); }
        finally { setSaving(false); }
    };

    const handleSaveEdit = async (id) => {
        if (!editText.trim()) return;
        try {
            const res = await fetch(`${API_URL}/notes/${id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ noteText: editText }) });
            if (!res.ok) throw new Error('Failed to update note');
            setEditingId(null); setEditText(''); fetchNotes();
        } catch (e) { alert(e.message); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            const res = await fetch(`${API_URL}/notes/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) fetchNotes();
        } catch (e) { alert(e.message); }
    };

    if (loading) return <p className="text-sm text-gray-400">Loading notes…</p>;

    return (
        <div className="space-y-3">
            {/* Add affordance */}
            {adding ? (
                <div className="space-y-2">
                    <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={3}
                        autoFocus
                        placeholder="Add a note…"
                        className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => { setAdding(false); setNewNote(''); }} className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                        <button onClick={handleAdd} disabled={saving} className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">{saving ? 'Saving…' : 'Add'}</button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setAdding(true)} className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-emerald-700 border border-dashed border-emerald-300 rounded hover:bg-emerald-50 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add note
                </button>
            )}

            {/* List */}
            {notes.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3">No notes yet</p>
            ) : (
                <div className="space-y-2">
                    {notes.map((note) => (
                        <div key={note.id} className="group px-2.5 py-2 bg-gray-50 rounded">
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-xs font-medium text-gray-900 truncate">{note.createdBy}</span>
                                    <span className="text-[10px] text-gray-400 flex-shrink-0">{new Date(note.createdAt).toLocaleDateString()}</span>
                                </div>
                                {editingId !== note.id && (
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                        <button onClick={() => { setEditingId(note.id); setEditText(note.noteText); }} className="p-1 text-gray-400 hover:text-emerald-600 rounded" title="Edit">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                        </button>
                                        <button onClick={() => handleDelete(note.id)} className="p-1 text-gray-400 hover:text-red-600 rounded" title="Delete">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            {editingId === note.id ? (
                                <div className="space-y-1.5">
                                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2}
                                        className="w-full px-2 py-1 text-sm bg-white border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => setEditingId(null)} className="px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200 rounded">Cancel</button>
                                        <button onClick={() => handleSaveEdit(note.id)} className="px-2 py-0.5 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700">Save</button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.noteText}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
